// @ts-check
// "Ver todos" do público (contrato 09 de público, seção 9): a lista paginada dos cards do mais novo, 50 por vez, pela
// mesma compilação da prévia; cada linha abre o card. No celular o diálogo ocupa a tela inteira.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { fetchAudienceCards } from '../../data/audiences.js';
import { findOption } from '../../lib/field-metadata.js';
import { formatDate, formatNumber } from '../../lib/format.js';
import { openRecord } from '../side-panel/open-record.js';
import { Modal } from '../../ui/modal.js';
import { Tag } from '../../ui/chip.js';
import { Skeleton } from '../../ui/feedback.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/audiences.js').AudienceCard} AudienceCard
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../../../shared/audiences/audience-definition.js').AudienceDefinition} AudienceDefinition
 */

/**
 * @param {{ definition: AudienceDefinition, usage: 'AUDIENCE' | 'CONDITION', stageField: FieldMetadata | undefined, onClose: () => void }} props
 */
export function AudienceCardsDialog({ definition, usage, stageField, onClose }) {
  const [cards, setCards] = useState(/** @type {AudienceCard[]} */ ([]));
  const [total, setTotal] = useState(/** @type {number | null} */ (null));
  const [nextCursor, setNextCursor] = useState(/** @type {string | null} */ (null));
  const [status, setStatus] = useState(/** @type {'loading' | 'ready' | 'error'} */ ('loading'));

  /** @param {string | null} cursor */
  const load = async (cursor) => {
    setStatus('loading');
    try {
      const page = await fetchAudienceCards({ definition, usage, cursor });
      setCards((current) => (cursor ? [...current, ...page.cards] : page.cards));
      if (page.total !== null) setTotal(page.total);
      setNextCursor(page.nextCursor);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => {
    load(null);
  }, []);

  return html`<${Modal} title=${t('audience.sample.dialogTitle')} subtitle=${total !== null ? t('audience.count.cards', { count: total, formatted: formatNumber(total) }) : undefined}
    onClose=${onClose} onConfirm=${onClose} confirmLabel=${t('common.close')} hideCancel=${true}>
    <div class="audience-cards-dialog">
      <div class="audience-cards-table" role="table" aria-label=${t('audience.sample.dialogTitle')}>
        <div class="audience-cards-row audience-cards-head" role="row">
          <span role="columnheader">${t('audience.sample.columns.card')}</span>
          <span role="columnheader">${t('audience.sample.columns.stage')}</span>
          <span role="columnheader">${t('audience.sample.columns.contact')}</span>
          <span role="columnheader">${t('audience.sample.columns.createdAt')}</span>
        </div>
        ${cards.map((card) => {
          const option = stageField && card.stage ? findOption(stageField, card.stage) : undefined;
          return html`<button type="button" role="row" class="audience-cards-row" key=${card.id}
            onClick=${() => {
              onClose();
              openRecord({ objectNameSingular: 'enrollmentOpportunity', recordId: card.id });
            }}>
            <span role="cell" class="audience-cards-name">${card.name || t('audience.sample.unnamed')}</span>
            <span role="cell">${option ? html`<${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} />` : null}</span>
            <span role="cell">${card.pointOfContactName ?? t('audience.sample.noContact')}</span>
            <span role="cell">${formatDate(card.createdAt)}</span>
          </button>`;
        })}
        ${status === 'loading' ? html`<div class="audience-cards-row" aria-busy="true"><${Skeleton} width="100%" height=${16} /></div>` : null}
      </div>
      ${status === 'error' ? html`<p class="audience-count-message" role="alert">${t('audience.count.error')}
        <button type="button" class="audience-link-button" onClick=${() => load(nextCursor)}>${t('audience.count.retry')}</button></p>` : null}
      ${nextCursor && status === 'ready'
        ? html`<button type="button" class="light-button audience-cards-more" onClick=${() => load(nextCursor)}>${t('audience.sample.loadMore')}</button>`
        : null}
    </div>
  <//>`;
}
